import Phaser from "phaser";

/**
 * Lucas's character animation (real art). A plain platformer set: idle / run /
 * jump, packed by `node tools/pack-character.mjs lucas` from the raw sheets in
 * ~/Downloads/lucas. Unlike Dan he has no throw, and unlike Andrew he faces both
 * ways, so this is the simplest of the three: run loops, jump is a rise/fall pose
 * pair chosen from vertical velocity (robust whatever the exact airtime), and idle
 * plays a one-shot "check the phone" fidget after a spell of standing still.
 *
 * Frame counts are read from the loaded atlas at register time, so re-packing with
 * a different number of frames needs no change here.
 */
export const LUCAS_ATLAS = "char-lucas";

// Shared character display size (Dan/Andrew use the same factor). The bike rig in
// Level2LucasScene multiplies its hand-authored alignment by this so seated Lucas
// stays matched to walking Lucas and the mount doesn't pop.
export const LUCAS_SCALE = 3.0;
export const LUCAS_ART = 3; // atlas pack multiple vs the original 64px (see pack-character.mjs)

// Playback speed (matches the other characters).
const FPS = 15;

// Rest on the first idle frame (plain standing pose); the fidget is timer-driven.
const IDLE_REST_FRAME = "lucas-idle-0";

// After this long standing perfectly still, play the idle fidget once, then reset
// to the rest frame and re-arm (so he periodically glances at his phone).
const IDLE_AFTER_MS = 10_000;

// Hitbox, fixed across states so level tuning never shifts. The packed frame is
// 39x64; this box is bottom-centered on his torso, feet at the frame bottom.
// Hitbox in frame px. Atlas is packed at CONTENT_H=192 (3x the old 64), so these are
// 3x their original values (20x44) and the sprite renders at LUCAS_SCALE/LUCAS_ART.
const BODY_W = 60;
const BODY_H = 132;

export const A = {
  idle: "lucas-idle",
  run: "lucas-run",
  rise: "lucas-rise",
  fall: "lucas-fall",
  climb: "lucas-climb",
};

/** Seat the art so the feet plant at the frame bottom while the hitbox stays put
 *  (origin + body offset move together). */
function seat(player: Phaser.Physics.Arcade.Sprite) {
  const nudge = 1;
  player.setOrigin(0.5, 0.5 - nudge / player.height);
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setOffset((player.width - BODY_W) / 2, player.height - BODY_H - nudge);
}

/** Register Lucas's animations once on the game-global animation manager. */
export function registerLucasAnims(scene: Phaser.Scene) {
  const anims = scene.anims;
  if (anims.exists(A.run)) return;

  const names = scene.textures.get(LUCAS_ATLAS).getFrameNames();
  const count = (state: string) =>
    names.filter((n) => new RegExp(`^lucas-${state}-\\d+$`).test(n)).length;

  // idle: a one-shot fidget (not a loop); the still state shows a single frame.
  const ic = count("idle");
  if (ic > 0)
    anims.create({
      key: A.idle,
      frames: anims.generateFrameNames(LUCAS_ATLAS, { prefix: "lucas-idle-", start: 0, end: ic - 1 }),
      frameRate: FPS,
      repeat: 0,
    });

  // run: looping cycle.
  const rc = count("run");
  if (rc > 0)
    anims.create({
      key: A.run,
      frames: anims.generateFrameNames(LUCAS_ATLAS, { prefix: "lucas-run-", start: 0, end: rc - 1 }),
      frameRate: FPS,
      repeat: -1,
    });

  // jump: a rise pose and a fall pose picked live from the jump sheet (no clip to
  // finish mid-air), so the pose always matches whether he's going up or down. The
  // two frames are chosen so his feet are drawn at nearly the same height (frame 1
  // rising ~31px off the frame bottom, frame 8 descending ~34px): the packer crops
  // every frame to one shared box without per-frame foot re-alignment, so poses with
  // very different feet heights would visibly pop him up/down at the apex. Keeping the
  // two matched removes that jump. (See pack-character.mjs shared-crop note.)
  const jc = count("jump");
  const pose = (key: string, t: number) => {
    if (jc > 0)
      anims.create({
        key,
        frames: [{ key: LUCAS_ATLAS, frame: `lucas-jump-${Math.floor((jc - 1) * t)}` }],
        frameRate: 1,
      });
  };
  pose(A.rise, 0.1); // -> frame 1 (feet ~31px off bottom)
  pose(A.fall, 0.58); // -> frame 8 (feet ~34px off bottom), matched to avoid an apex pop

  // climb: looping cycle played while on a ladder.
  const cc = count("climb");
  if (cc > 0)
    anims.create({
      key: A.climb,
      frames: anims.generateFrameNames(LUCAS_ATLAS, { prefix: "lucas-climb-", start: 0, end: cc - 1 }),
      frameRate: FPS,
      repeat: -1,
    });
}

/** Point a freshly-created player sprite at Lucas's atlas + hitbox. */
export function useLucasSprite(player: Phaser.Physics.Arcade.Sprite) {
  player.setTexture(LUCAS_ATLAS, IDLE_REST_FRAME);
  const body = player.body as Phaser.Physics.Arcade.Body;
  body.setSize(BODY_W, BODY_H);
  player.setFrame(IDLE_REST_FRAME);
  seat(player);
  player.setScale(LUCAS_SCALE / LUCAS_ART); // art packed 3x, render near 1:1 (crisp)
  player.setData("stillSince", undefined);
  player.setData("fidgeting", false);
}

/** Per-frame: choose idle / run / jump from physics state and face travel. Call
 *  once per update() after movement is applied (and only while Lucas is on foot;
 *  when he's mounted on the bike the scene hides him and this isn't called). */
export function updateLucasAnim(player: Phaser.Physics.Arcade.Sprite) {
  const body = player.body as Phaser.Physics.Arcade.Body;
  const vx = body.velocity.x;
  const moving = Math.abs(vx) > 5;
  if (moving) player.setFlipX(vx < 0); // art faces right; keep last facing when still

  const cur = player.anims.currentAnim?.key;
  const now = player.scene.time.now;
  const busy = () => {
    player.setData("stillSince", undefined);
    player.setData("fidgeting", false);
  };

  // airborne: rise while going up, fall while coming down.
  if (!body.blocked.down) {
    busy();
    const want = body.velocity.y < -20 ? A.rise : A.fall;
    seat(player);
    if (cur !== want) player.play(want, true);
    return;
  }

  // grounded + moving: run loop (replay if it was left stopped, so it never freezes
  // into a slide after standing still).
  if (moving) {
    busy();
    seat(player);
    if (cur !== A.run || !player.anims.isPlaying) player.play(A.run, true);
    return;
  }

  // grounded + still: hold the rest frame; after IDLE_AFTER_MS play the phone-check
  // fidget once, then reset and re-arm.
  seat(player);
  if (player.getData("fidgeting")) {
    if (player.anims.isPlaying && cur === A.idle) return; // let it play out
    player.setData("fidgeting", false);
    player.anims.stop();
    player.setFrame(IDLE_REST_FRAME);
    player.setData("stillSince", now); // re-arm the next fidget
    return;
  }
  if (player.getData("stillSince") === undefined) {
    player.setData("stillSince", now);
    player.anims.stop();
    player.setFrame(IDLE_REST_FRAME);
    return;
  }
  if (now - (player.getData("stillSince") as number) >= IDLE_AFTER_MS) {
    player.setData("fidgeting", true);
    player.play(A.idle);
  }
}
